import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Modal,
  Form,
  Select,
  Tag,
  Avatar,
  Typography,
  Row,
  Col,
  Statistic,
  Dropdown,
  message,
  Popconfirm,
  Drawer,
  Tabs,
  Timeline,
  List,
  Badge
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  BuildOutlined,
  MoreOutlined,
  EyeOutlined,
  StarOutlined,
  StarFilled,
  CalendarOutlined,
  DollarOutlined,
  TeamOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Search } = Input;

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  status: 'active' | 'inactive' | 'prospect' | 'customer';
  source: 'website' | 'referral' | 'cold_call' | 'social_media' | 'event' | 'other';
  tags: string[];
  notes: string;
  assignedTo: string;
  createdAt: string;
  lastContact: string;
  totalValue: number;
  avatar?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
}

interface ContactActivity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'note' | 'task';
  description: string;
  date: string;
  outcome?: string;
}

// Sample data
const sampleContacts: Contact[] = [
  {
    id: '1',
    firstName: 'John',
    lastName: 'Smith',
    email: 'john.smith@techsolutions.com',
    phone: '+1 (555) 123-4567',
    company: 'Tech Solutions Inc',
    position: 'CTO',
    status: 'customer',
    source: 'website',
    tags: ['enterprise', 'tech', 'decision-maker'],
    notes: 'Interested in our enterprise solution. Has budget approved.',
    assignedTo: 'Sarah Johnson',
    createdAt: '2024-01-15',
    lastContact: '2024-06-20',
    totalValue: 45000,
    address: {
      street: '123 Tech Street',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94105',
      country: 'USA'
    }
  },
  {
    id: '2',
    firstName: 'Emily',
    lastName: 'Davis',
    email: 'emily.davis@globalretail.com',
    phone: '+1 (555) 987-6543',
    company: 'Global Retail Ltd',
    position: 'Operations Manager',
    status: 'prospect',
    source: 'referral',
    tags: ['retail', 'operations', 'warm-lead'],
    notes: 'Referred by existing customer. Needs inventory management solution.',
    assignedTo: 'Mike Chen',
    createdAt: '2024-02-10',
    lastContact: '2024-06-18',
    totalValue: 0
  },
  {
    id: '3',
    firstName: 'Robert',
    lastName: 'Johnson',
    email: 'robert.j@manufacturing.com',
    phone: '+1 (555) 456-7890',
    company: 'Manufacturing Corp',
    position: 'Plant Manager',
    status: 'active',
    source: 'cold_call',
    tags: ['manufacturing', 'production', 'follow-up'],
    notes: 'Interested in manufacturing module. Schedule demo next week.',
    assignedTo: 'Lisa Wang',
    createdAt: '2024-03-05',
    lastContact: '2024-06-22',
    totalValue: 12000
  }
];

const ContactsManagement: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>(sampleContacts);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>(sampleContacts);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailDrawerVisible, setIsDetailDrawerVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [form] = Form.useForm();

  // Filter contacts based on search and status
  useEffect(() => {
    let filtered = contacts;

    if (searchText) {
      filtered = filtered.filter(contact =>
        contact.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
        contact.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
        contact.email.toLowerCase().includes(searchText.toLowerCase()) ||
        contact.company.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(contact => contact.status === statusFilter);
    }

    setFilteredContacts(filtered);
  }, [contacts, searchText, statusFilter]);

  const handleAddContact = () => {
    setSelectedContact(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    form.setFieldsValue(contact);
    setIsModalVisible(true);
  };

  const handleDeleteContact = (id: string) => {
    setContacts(contacts.filter(contact => contact.id !== id));
    message.success('Contact deleted successfully');
  };

  const handleViewContact = (contact: Contact) => {
    setSelectedContact(contact);
    setIsDetailDrawerVisible(true);
  };

  const handleModalOk = () => {
    form.validateFields().then(values => {
      if (selectedContact) {
        // Edit existing contact
        setContacts(contacts.map(contact =>
          contact.id === selectedContact.id
            ? { ...contact, ...values }
            : contact
        ));
        message.success('Contact updated successfully');
      } else {
        // Add new contact
        const newContact: Contact = {
          id: Date.now().toString(),
          ...values,
          createdAt: new Date().toISOString().split('T')[0],
          lastContact: new Date().toISOString().split('T')[0],
          totalValue: 0,
          tags: values.tags || []
        };
        setContacts([...contacts, newContact]);
        message.success('Contact added successfully');
      }
      setIsModalVisible(false);
      form.resetFields();
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'customer': return 'green';
      case 'prospect': return 'blue';
      case 'active': return 'orange';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'website': return '🌐';
      case 'referral': return '👥';
      case 'cold_call': return '📞';
      case 'social_media': return '📱';
      case 'event': return '🎪';
      default: return '📝';
    }
  };

  const columns: ColumnsType<Contact> = [
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <Space>
          <Avatar 
            src={record.avatar} 
            icon={<UserOutlined />}
            style={{ backgroundColor: '#1890ff' }}
          >
            {record.firstName[0]}{record.lastName[0]}
          </Avatar>
          <div>
            <Text strong>{record.firstName} {record.lastName}</Text>
            <br />
            <Text type="secondary">{record.position}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      render: (company) => (
        <Space>
                          <BuildOutlined />
          {company}
        </Space>
      ),
    },
    {
      title: 'Contact Info',
      key: 'contactInfo',
      render: (_, record) => (
        <div>
          <div>
            <MailOutlined style={{ marginRight: 8 }} />
            <Text copyable>{record.email}</Text>
          </div>
          <div style={{ marginTop: 4 }}>
            <PhoneOutlined style={{ marginRight: 8 }} />
            <Text copyable>{record.phone}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (source) => (
        <Space>
          <span>{getSourceIcon(source)}</span>
          <Text>{source.replace('_', ' ').toUpperCase()}</Text>
        </Space>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'totalValue',
      key: 'totalValue',
      render: (value) => (
        <Text strong style={{ color: value > 0 ? '#52c41a' : '#8c8c8c' }}>
          ${value.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Last Contact',
      dataIndex: 'lastContact',
      key: 'lastContact',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewContact(record)}
          />
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => handleEditContact(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this contact?"
            onConfirm={() => handleDeleteContact(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Stats calculations
  const totalContacts = contacts.length;
  const customers = contacts.filter(c => c.status === 'customer').length;
  const prospects = contacts.filter(c => c.status === 'prospect').length;
  const totalValue = contacts.reduce((sum, c) => sum + c.totalValue, 0);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Contacts"
              value={totalContacts}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Customers"
              value={customers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Prospects"
              value={prospects}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Value"
              value={totalValue}
              prefix={<DollarOutlined />}
              formatter={(value) => `$${Number(value).toLocaleString()}`}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={3} style={{ margin: 0 }}>Contacts Management</Title>
            </Col>
            <Col>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleAddContact}
              >
                Add Contact
              </Button>
            </Col>
          </Row>
        </div>

        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Search
              placeholder="Search contacts..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={4}>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Status</Option>
              <Option value="customer">Customer</Option>
              <Option value="prospect">Prospect</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Col>
        </Row>

        {/* Contacts Table */}
        <Table
          columns={columns}
          dataSource={filteredContacts}
          rowKey="id"
          pagination={{
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contacts`,
          }}
        />
      </Card>

      {/* Add/Edit Contact Modal */}
      <Modal
        title={selectedContact ? 'Edit Contact' : 'Add New Contact'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter first name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter last name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="company"
                label="Company"
                rules={[{ required: true, message: 'Please enter company' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="position"
                label="Position"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="prospect">Prospect</Option>
                  <Option value="active">Active</Option>
                  <Option value="customer">Customer</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="source"
                label="Source"
                rules={[{ required: true, message: 'Please select source' }]}
              >
                <Select>
                  <Option value="website">Website</Option>
                  <Option value="referral">Referral</Option>
                  <Option value="cold_call">Cold Call</Option>
                  <Option value="social_media">Social Media</Option>
                  <Option value="event">Event</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="assignedTo"
            label="Assigned To"
          >
            <Select>
              <Option value="Sarah Johnson">Sarah Johnson</Option>
              <Option value="Mike Chen">Mike Chen</Option>
              <Option value="Lisa Wang">Lisa Wang</Option>
              <Option value="David Brown">David Brown</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Contact Detail Drawer */}
      <Drawer
        title="Contact Details"
        placement="right"
        width={600}
        open={isDetailDrawerVisible}
        onClose={() => setIsDetailDrawerVisible(false)}
      >
        {selectedContact && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Avatar 
                size={80} 
                src={selectedContact.avatar}
                icon={<UserOutlined />}
                style={{ backgroundColor: '#1890ff' }}
              >
                {selectedContact.firstName[0]}{selectedContact.lastName[0]}
              </Avatar>
              <Title level={4} style={{ marginTop: 16 }}>
                {selectedContact.firstName} {selectedContact.lastName}
              </Title>
              <Text type="secondary">{selectedContact.position}</Text>
              <br />
              <Tag color={getStatusColor(selectedContact.status)} style={{ marginTop: 8 }}>
                {selectedContact.status.toUpperCase()}
              </Tag>
            </div>

            <Tabs defaultActiveKey="1">
              <TabPane tab="Contact Info" key="1">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Company:</Text>
                    <br />
                    <Text>{selectedContact.company}</Text>
                  </div>
                  <div>
                    <Text strong>Email:</Text>
                    <br />
                    <Text copyable>{selectedContact.email}</Text>
                  </div>
                  <div>
                    <Text strong>Phone:</Text>
                    <br />
                    <Text copyable>{selectedContact.phone}</Text>
                  </div>
                  <div>
                    <Text strong>Source:</Text>
                    <br />
                    <Text>{getSourceIcon(selectedContact.source)} {selectedContact.source.replace('_', ' ').toUpperCase()}</Text>
                  </div>
                  <div>
                    <Text strong>Assigned To:</Text>
                    <br />
                    <Text>{selectedContact.assignedTo}</Text>
                  </div>
                  <div>
                    <Text strong>Total Value:</Text>
                    <br />
                    <Text strong style={{ color: '#52c41a' }}>
                      ${selectedContact.totalValue.toLocaleString()}
                    </Text>
                  </div>
                  {selectedContact.notes && (
                    <div>
                      <Text strong>Notes:</Text>
                      <br />
                      <Text>{selectedContact.notes}</Text>
                    </div>
                  )}
                </Space>
              </TabPane>
              <TabPane tab="Activity" key="2">
                <Timeline>
                  <Timeline.Item color="green">
                    <Text strong>Contact Created</Text>
                    <br />
                    <Text type="secondary">{new Date(selectedContact.createdAt).toLocaleDateString()}</Text>
                  </Timeline.Item>
                  <Timeline.Item color="blue">
                    <Text strong>Last Contact</Text>
                    <br />
                    <Text type="secondary">{new Date(selectedContact.lastContact).toLocaleDateString()}</Text>
                  </Timeline.Item>
                </Timeline>
              </TabPane>
            </Tabs>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ContactsManagement; 